import { BlinkoGameEngine } from './game-engine';
import { BIN_MULTIPLIERS, type BlinkoLetter, type BlinkoVersion } from './constants';
import { GameEvent } from './events';
import { deriveSeed } from '../utils';

export interface SimulationResult {
  /** physics steps the ball took to land (game loop runs at 60fps, so steps/60 s). */
  steps: number;
  finalBin: number;
  multiplier: number;
  specialHits: number;
  binMultiplier: string;
  lettersHit: BlinkoLetter[];
  seedUsed: number;
}

function runSingleSimulation(version: BlinkoVersion
  , seed: number, letterSeed?: number, spin?: number): SimulationResult {
  const engine = new BlinkoGameEngine();
  engine.setVersion(version);

  if (letterSeed) {
    engine.initializeLetters(letterSeed);
  }

  let finalBin = -1;
  let finalMultiplier = 0;
  let totalSpecialHits = 0;
  const lettersHit: BlinkoLetter[] = [];

  engine.on('ball-hit-bin', (event: GameEvent) => {
    if (event.type === 'ball-hit-bin' && event.payload) {
      finalBin = event.payload.bin;
      finalMultiplier = parseFloat(BIN_MULTIPLIERS[event.payload.bin]);
    }
  });

  engine.on('special-pin-hit', () => {
    totalSpecialHits++;
  });

  engine.on('letter-activated', (event: GameEvent) => {
    if (event.type === 'letter-activated') {
      lettersHit.push(event.payload.letter);
    }
  });

  engine.addBall(seed, spin);

  const maxIterations = 10000;
  let iterations = 0;

  while (engine.getBallCount() > 0 && iterations < maxIterations) {
    engine.update();
    iterations++;
  }

  if (iterations >= maxIterations) {
    console.warn(`Simulation reached max iterations (${maxIterations}) for seed ${seed}`);
  }

  return {
    steps: iterations,
    finalBin,
    multiplier: finalMultiplier,
    specialHits: totalSpecialHits,
    binMultiplier: BIN_MULTIPLIERS[finalBin] || '0',
    lettersHit,
    seedUsed: seed,
  };
}

export function simulateBall(version: BlinkoVersion, seed: number, letterSeed?: number, spin?: number): SimulationResult {
  let tryCount = 0;
  while (true) {
    const result = runSingleSimulation(version, seed, letterSeed, spin);
    if (result.finalBin !== -1) {
      return { ...result, seedUsed: seed };
    }
    tryCount += 1;
    seed = deriveSeed(seed, tryCount, 0x42);
  }
}
